import { Component } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { AuthService, EventDto, EventManagementService } from '@entscheidungsnavi/api-client';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, shareReplay } from 'rxjs';

@Component({
  templateUrl: './event-overview.component.html',
  styleUrls: ['./event-overview.component.scss'],
  standalone: false,
})
export class EventOverviewComponent {
  protected displayedColumns = ['submissionStatus', 'name', 'code', 'registrationCount', 'submissionCount', 'createdAt'] as const;

  protected displayedEvents$: Observable<EventDto[]>;
  protected activeSort$ = new BehaviorSubject<{
    active: (typeof EventOverviewComponent.prototype.displayedColumns)[number];
    direction: SortDirection;
  }>({
    active: 'createdAt',
    direction: 'desc',
  });

  protected showOnlyEventsUserIsParticipatingIn$ = new BehaviorSubject<boolean>(true);

  protected hasError = false;

  protected userIsAdmin$ = this.authService.userHasRole('admin');

  readonly canCreateEvents$ = this.authService.userHasRole('event-manager');

  constructor(
    eventService: EventManagementService,
    private authService: AuthService,
  ) {
    this.displayedEvents$ = combineLatest([eventService.events$, this.activeSort$, this.showOnlyEventsUserIsParticipatingIn$]).pipe(
      map(([events, sort, showOnlyParticipating]) => {
        let displayedEvents = events.list.slice();

        if (showOnlyParticipating) {
          const userEmail = this.authService.user?.email;
          if (userEmail == null) {
            return [];
          }

          displayedEvents = displayedEvents.filter(event => {
            return (
              event.editors.some(editor => editor.email === userEmail) ||
              event.viewers.some(viewer => viewer.email === userEmail) ||
              event.owner?.email === userEmail
            );
          });
        }

        displayedEvents.sort((a, b) => {
          let result = 0;

          switch (sort.active) {
            case 'submissionStatus':
              result = +(a.submissionStatus === 'open') - +(b.submissionStatus === 'open');
              break;
            case 'name':
              result = a.name.localeCompare(b.name);
              break;
            case 'code':
              if (a.code == null || b.code == null) {
                return a.code === b.code ? 0 : a.code == null ? 1 : -1;
              }

              result = a.code.localeCompare(b.code);
              break;
            case 'registrationCount':
              result = a.registrationCount - b.registrationCount;
              break;
            case 'submissionCount':
              result = a.submissionCount - b.submissionCount;
              break;
            case 'createdAt':
              result = a.createdAt.getTime() - b.createdAt.getTime();
              break;
          }

          return result * (sort.direction === 'asc' ? 1 : -1);
        });

        return displayedEvents;
      }),
      catchError(error => {
        console.error(error);
        this.hasError = true;
        return of();
      }),
      shareReplay(1),
    );
  }
}
